import { LitElement, html, nothing, type TemplateResult } from "lit";
import type {
  DeferredMembershipPreviewDto,
  DeferredMembershipPurchaseDto,
} from "@zipper/embedded-api-client";
import {
  createDeferredCheckoutSetup,
  deferredCheckoutError,
  deferredTermsNeedReview,
  deferredQuoteTotals,
  previewDeferredCheckout,
  reserveDeferredCheckout,
  type DeferredCheckoutTarget,
} from "../../payments/deferred-checkout";
import { customElement, property, state } from "lit/decorators.js";
import { consume } from "@lit/context";
import { createActorState, type ActorStateOf } from "../../state/actor-state";
import type {
  Stripe,
  StripeCardNumberElement,
  StripeCardExpiryElement,
  StripeCardCvcElement,
  StripeElements,
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
  StripePaymentRequestButtonElement,
} from "@stripe/stripe-js";
import {
  createAchSetupIntent,
  createGuestRoster,
  isAccountExistsError,
  type GuestRosterHandle,
  loadEmbedStripe,
  previewCharge,
  runGuestCheckout,
  runMemberCheckout,
  type ChargeItem,
  type GuestDetails,
} from "../../payments/stripe-client";
import {
  estimatedTotals,
  formatCents,
  previewRejection,
  totalsFromPreview,
  type OrderTotals,
} from "../../checkout/order-totals";
import { accountExistsMessage } from "../../checkout/account-exists-copy";
import type { RequiredSignupFields } from "../../config/types";
import {
  emptyRequiredProfile,
  isPhoneRequired,
  isRequiredProfileComplete,
  mergeRequiredProfile,
  requiredProfilePayload,
} from "../../checkout/required-profile";
import { openSignIn } from "./open-signin";
import type { EmbedStripe } from "../../config/types";
import type { MemberContext, SavedCard } from "../../data/live";
import * as checkoutMath from "../../checkout/checkout-math";
import { CheckoutSubmissionController } from "../../checkout/submission.machine";
import {
  createPublicEmbedRuntime,
  embedRuntimeContext,
  runtimeApiArguments,
  runtimeApiOption,
  type EmbedRuntime,
} from "../../runtime/context";
import { paymentStyles } from "./payment/payment.styles";
import {
  renderBankAccountForm,
  renderPaymentCustomerDetails,
  renderPaymentPhoneField,
  renderPaymentSubmit,
  renderPaymentTypeSelector,
  renderPaymentUnavailable,
  renderSavedCardPicker,
  type PaymentType,
} from "./payment/payment.views";
import {
  PaymentPortalController,
  type PaymentPortalKey,
} from "./payment/payment-portal.controller";

/**
 * Height Stripe renders the Payment Request (wallet) button at. Shared
 * between the Stripe `style` option (`mountWalletButton`) and the in-template
 * `#zpay-prbutton` slot's `min-height`, so the slot reserves the exact space
 * the (possibly slot-projected, see B2) mounted button will actually occupy.
 */
const PR_BUTTON_HEIGHT = "44px";

/**
 * The API's own message for a failed post-charge step. The generated SDK
 * throws a `ResponseError` whose `.message` is generic and whose parsed body
 * carries the real reason; anything else falls back to the thrown message.
 */
function confirmationErrorReason(error: unknown): string {
  const body = (error as { body?: { message?: unknown } } | null)?.body;
  const message = Array.isArray(body?.message)
    ? body.message.join(" ")
    : typeof body?.message === "string"
      ? body.message
      : error instanceof Error
        ? error.message
        : "";
  const reason = message.trim();
  if (!reason || /response returned an error code/i.test(reason)) return "";
  return /[.!?]$/.test(reason) ? reason : `${reason}.`;
}

/** Common lifecycle signals supplied by each of Stripe's card field types. */
interface CardFieldLifecycle {
  on(event: "ready", handler: () => void): unknown;
  on(event: "focus", handler: () => void): unknown;
  on(event: "blur", handler: () => void): unknown;
  on(
    event: "change",
    handler: (event: {
      complete: boolean;
      error?: { message: string };
    }) => void,
  ): unknown;
}

/** Who a quote is drafted against, and the capability that authorizes it. */
interface QuoteTarget {
  purchasedForRosterId: number;
  checkoutToken?: string | undefined;
  stripeCustomerId: string;
}

export type BookingPaymentPrerequisites = { ready: boolean; key: string };

const paymentActorState = createActorState(() => ({
  status: "loading" as "idle" | "loading" | "paying" | "done",
  error: null as string | null,
  draft: { firstName: "", lastName: "", email: "", phone: "" },
  requiredProfile: emptyRequiredProfile(),
  selectedCardId: "",
  applePay: false,
  googlePay: false,
  walletProbeDone: false,
  payType: "card" as PaymentType,
  cardComplete: false,
  cardFieldError: "",
  cardReady: false,
  /** ACH account holder name, once the buyer edits the prefilled one. */
  bankAccountName: "",
  billingPostalCode: "",
  /** Server quote for this cart; null until one has been fetched. */
  quote: null as OrderTotals | null,
  quoteError: null as string | null,
  deferredPreview: null as DeferredMembershipPreviewDto | null,
  deferredConsentAccepted: false,
}));

type PaymentActorState = ActorStateOf<typeof paymentActorState>;

/**
 * Real payment overlay — Stripe Elements (card) + Connect charge via the anon
 * guest endpoints. Renders in **light DOM** (Stripe Elements is unreliable in
 * shadow DOM) as a fixed modal appended to the host page by the checkout flows.
 *
 * Wallets mirror dashboard InAppCheckout: Stripe `canMakePayment()` surfaces
 * Apple Pay / Google Pay explicitly; PaymentRequest rebuilds when the total
 * changes (coupon/discount) so the wallet sheet authorizes the right amount.
 *
 * Events: `zipper-pay-success` (charged), `zipper-pay-close` (dismissed).
 *
 * B2 (Stripe "Elements cannot be mounted in a ShadowRoot"): opting THIS
 * component out of shadow attachment isn't sufficient on its own, because the
 * checkout flows above it still nest it inside their own shadow root when
 * presenting it inline (see `isInShadowTree` above). `mountCard` /
 * `mountWalletButton` route through `resolveMountTarget`, which — only when
 * the in-template slot (`#zpay-card` / `#zpay-prbutton`) is actually
 * shadow-rooted — creates a document-rooted div as a light child of the
 * outermost shadow host and projects it back into the slot through a chain
 * of named `<slot>`s (see PaymentPortalController). The iframe lays out in
 * normal flow at the slot, so nothing has to track its position. Everything
 * else (modal chrome, buttons, copy, the `accent`/theme tokens) stays exactly
 * where the parent placed it, so theming is unaffected.
 * Rejected alternatives: (a) a `position: fixed` overlay kept pixel-aligned
 * over the slot each frame — shipped first, but iOS Safari moves fixed layers
 * independently of the page around the keyboard, so the iframes drifted off
 * their boxes (PLAT-7133); (b) a same-origin iframe adds real complexity
 * (loading stripe.js into a second document, CSS/theme tokens no longer
 * cascade across the iframe boundary, extra a11y/focus-trap work) for no
 * benefit over slot projection.
 */
@customElement("zipper-payment")
export class ZipperPayment extends LitElement {
  // Light DOM so Stripe Elements mounts in a normal document.
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  @property({ attribute: false }) stripe?: EmbedStripe;
  @consume({ context: embedRuntimeContext, subscribe: true })
  @property({ attribute: false })
  runtime: EmbedRuntime = createPublicEmbedRuntime();
  @property({ type: Number }) orgId = 0;
  @property({ attribute: false }) items: ChargeItem[] = [];
  @property({ type: Boolean }) bookingCheckout = false;
  /** Current host answers/consent, independent of persistence-generated IDs. */
  @property({ attribute: false }) bookingPrerequisites?: BookingPaymentPrerequisites;
  @property({ attribute: false }) afterDeferredPurchase?: (
    purchase: DeferredMembershipPurchaseDto,
  ) => Promise<void>;
  @property({ attribute: false }) customer: GuestDetails & { phone?: string } =
    {
      firstName: "",
      lastName: "",
      email: "",
    };
  @property({ type: String }) accent = "#4F9DB5";
  @property({ type: String }) heading = "Payment";
  /** Validated Stripe coupon id — applied invoice-wide by the backend. */
  @property({ type: String }) couponId = "";
  /** Dollar discount the coupon yields (for display only; Stripe computes the real one). */
  @property({ type: Number }) discount = 0;
  /** Optional post-charge step (register class/event + confirm spot). Receives invoice ids aligned to `items`. */
  @property({ attribute: false }) afterCharge?: (
    invoiceIds: (string | undefined)[]
  ) => Promise<void>;
  /**
   * Optional host-owned prerequisite for a charge. Public product checkout
   * uses this to persist a required form and refresh `items` before money
   * moves. It is deliberately shared by card, saved-card, and wallet paths.
   */
  @property({ attribute: false }) beforeCharge?: () => Promise<void>;
  /**
   * Signed-in member's saved cards (Phase 4). ADDITIVE: empty by default →
   * the component behaves exactly like the guest flow. When non-empty, a
   * saved-card picker is shown and the member can pay with a card on file.
   */
  @property({ attribute: false }) savedCards: SavedCard[] = [];
  /** Signed-in member context (roster + customer id) for the saved-card charge. */
  @property({ attribute: false }) memberContext?: MemberContext;
  /**
   * ST3: when the org's embed requires a phone number, block Pay until a valid
   * phone is present — either pre-filled by the caller (`customer.phone`) or
   * entered inline here — and surface the phone field marked required.
   */
  @property({ type: Boolean }) requirePhone = false;
  /**
   * PLAT-4456 org policy from `EmbedConfig.requiredSignupFields`. Separate from
   * `requirePhone` above, which is the embedding site's own toggle: the API
   * enforces THIS one at `create-guest`, so a checkout that doesn't collect it
   * fails after the customer has filled in everything else.
   */
  @property({ attribute: false }) requiredSignupFields?: RequiredSignupFields;
  /** Org ISO-4217 currency (`EmbedConfig.currency`); USD when unset. */
  @property({ type: String }) currency = "";
  /** Assign the purchase to a different roster (guest/family recipient). */
  @property({ type: Number }) purchasedForRosterId = 0;
  /**
   * A guest roster the host already created for this checkout.
   *
   * `<zipper-booking-checkout>` mints one before submitting intake forms (so
   * the responses can be linked to the customer), and hands it over here.
   * Charging must reuse it: a second `create-guest` for the same email answers
   * 401 `guest_checkout_account_exists`, which would fail the payment of a
   * first-time booker who has done nothing wrong. Unset for hosts that don't
   * pre-create one — the charge then mints its own exactly as before.
   */
  @property({ attribute: false }) guestRoster?: GuestRosterHandle;

  private paidInvoiceIds: (string | undefined)[] | null = null;

  @paymentActorState.field("status")
  private status!: PaymentActorState["status"];

  @paymentActorState.field("error")
  private error!: PaymentActorState["error"];

  /** Collected inline when the caller didn't pre-fill customer details (e.g. shop). */
  @paymentActorState.field("draft")
  private draft!: PaymentActorState["draft"];
  private phoneEdited = false;

  @paymentActorState.field("requiredProfile")
  private requiredProfile!: PaymentActorState["requiredProfile"];

  /** Selected saved-card PaymentMethod id; '' = enter a new card. */
  @paymentActorState.field("selectedCardId")
  private selectedCardId!: PaymentActorState["selectedCardId"];

  /** Device wallet availability from Stripe `canMakePayment()` (not UA sniffing). */
  @paymentActorState.field("applePay")
  private applePay!: PaymentActorState["applePay"];

  @paymentActorState.field("googlePay")
  private googlePay!: PaymentActorState["googlePay"];

  @paymentActorState.field("walletProbeDone")
  private walletProbeDone!: PaymentActorState["walletProbeDone"];

  /** Selected pay path — mirrors PaymentTypeSelector (card / Apple / Google). */
  @paymentActorState.field("payType")
  private payType!: PaymentActorState["payType"];
  /**
   * B4: Stripe's own verdict on the typed card, mirrored from the Element's
   * `change` feed. The number/expiry/CVC live in a cross-origin iframe, so this
   * is the ONLY signal that distinguishes "the card method is selected" from
   * "the card method can actually be charged".
   */
  @paymentActorState.field("cardComplete")
  private cardComplete!: PaymentActorState["cardComplete"];

  /** B4: Stripe's in-field validation message, if any ("Your card number is incomplete."). */
  @paymentActorState.field("cardFieldError")
  private cardFieldError!: PaymentActorState["cardFieldError"];
  /**
   * B5: Stripe has emitted `ready` for the CURRENT Element — i.e. the iframe is
   * live and `createPaymentMethod` can actually read it. Reading an Element
   * before this fails with "We could not retrieve data from the specified
   * Element. Please make sure the Element you are attempting to use is mounted
   * and the ready event has been emitted."
   */
  @paymentActorState.field("cardReady")
  private cardReady!: PaymentActorState["cardReady"];

  @paymentActorState.field("billingPostalCode")
  private billingPostalCode!: PaymentActorState["billingPostalCode"];

  /**
   * What the server says this cart costs — tax and pass-on fees included.
   * Null until a quote lands (or when there is no roster to quote against), in
   * which case the summary shows the cart's own arithmetic and says plainly
   * that tax and fees are still to be calculated.
   */
  @paymentActorState.field("quote")
  private quote!: PaymentActorState["quote"];

  @paymentActorState.field("quoteError")
  private quoteError!: PaymentActorState["quoteError"];

  @paymentActorState.field("bankAccountName")
  private bankAccountName!: PaymentActorState["bankAccountName"];
  private bankAccountNameEdited = false;
  /**
   * Bank account Financial Connections already verified and saved. A charge
   * that fails after verification reuses it rather than re-opening the bank
   * modal.
   */
  private bankPaymentMethodId = "";

  @paymentActorState.field("deferredPreview")
  private deferredPreview!: PaymentActorState["deferredPreview"];
  @paymentActorState.field("deferredConsentAccepted")
  private deferredConsentAccepted!: boolean;
  @state() private deferredTermsChanged = false;
  private deferredAttemptId?: string;
  private confirmedDeferredSetup?: {
    setupIntentId: string;
    paymentMethodId: string;
  };
  private reservedDeferredPurchase?: DeferredMembershipPurchaseDto;
  private reservedDeferredKey = "";
  /**
   * B4/B5: true once we successfully subscribed to a card Element's lifecycle
   * feed (`change` + `ready`). Real Stripe.js Elements always support it; a
   * bare `{ mount, unmount }` test double does not. Without the feed we have no
   * validity signal at all, so those two gates stay OFF rather than
   * dead-locking the parent's CTA behind events that can never arrive.
   */
  private cardReportsChanges = false;

  /**
   * Every typed-card, saved-card, and wallet charge passes through the same
   * semantic statechart. Its synchronous transition out of `editing` is the
   * single-flight guard and its context owns the server idempotency key.
   */
  private readonly submission = new CheckoutSubmissionController(
    this,
    "product"
  );

  private stripeApi: Stripe | null = null;
  private elements: StripeElements | null = null;
  private cardEl: StripeCardNumberElement | null = null;
  private cardExpiryEl: StripeCardExpiryElement | null = null;
  private cardCvcEl: StripeCardCvcElement | null = null;
  private readonly cardValidity = {
    number: { ready: false, complete: false, error: "" },
    expiry: { ready: false, complete: false, error: "" },
    cvc: { ready: false, complete: false, error: "" },
  };
  private cardMounted = false;
  /**
   * B5: the node the current card Element was mounted into. A detached target
   * means Stripe can no longer read the Element even though we still hold it.
   */
  private cardMountTarget: HTMLElement | null = null;
  private cardDetailMountTargets: HTMLElement[] = [];
  private paymentRequest: PaymentRequest | null = null;
  private prButtonEl: StripePaymentRequestButtonElement | null = null;
  private prButtonMounted = false;
  /** Cents last used to build PaymentRequest — refresh when total changes. */
  private walletAmountCents = -1;
  private walletSetupGen = 0;
  private walletSetupContext = "";

  private readonly portalController = new PaymentPortalController(this);

  private get subtotal(): number {
    return checkoutMath.sumItems(this.items);
  }

  private get deferredItem(): ChargeItem | undefined {
    return this.items.find((item) => item.deferredChargeSettings?.enabled);
  }

  private get validDeferredCart(): boolean {
    return (
      this.items.length === 1 &&
      (this.items[0]?.quantity ?? 1) === 1 &&
      !this.couponId &&
      !this.discount &&
      !this.items[0]?.coupon
    );
  }

  private deferredTarget(): DeferredCheckoutTarget {
    const item = this.deferredItem;
    const rosterId = this.memberContext?.rosterId ?? this.guestRoster?.rosterId;
    if (!item?.deferredChargeSettings || !rosterId)
      throw new Error(
        "Please complete your customer details before reserving this membership.",
      );
    return {
      orgId: this.orgId,
      rosterId,
      purchasedForRosterId: this.purchasedForRosterId || undefined,
      checkoutToken: this.memberContext
        ? undefined
        : this.guestRoster?.checkoutToken,
      stripeProductId: item.stripeProductId,
      settings: item.deferredChargeSettings,
      bookingCheckout: this.bookingCheckout,
      ...runtimeApiOption(this.runtime),
    };
  }

  private get payable(): boolean {
    return (
      !!this.stripe &&
      this.items.length > 0 &&
      this.items.every((i) => !!i.stripeProductId)
    );
  }

  /**
   * True when the member is NOT paying with a saved card: a fresh card or a
   * wallet (a wallet has no saved-card id either).
   */
  private get usingNewCard(): boolean {
    return !this.selectedCardId;
  }

  /** Caller didn't supply customer details → collect them in the modal. */
  private get needsDetails(): boolean {
    // A saved card belongs to the signed-in member — identity is already known.
    if (!this.usingNewCard) return false;
    return !this.customer.email;
  }

  /** Effective customer (pre-filled by caller, or collected inline). */
  private get effectiveCustomer(): GuestDetails & { phone?: string } {
    return this.needsDetails ? this.draft : this.customer;
  }

  /**
   * ST3: the phone the charge should carry — the inline draft entry wins over a
   * caller-supplied one so the standalone "phone required" field (shown when the
   * caller pre-filled everything BUT the phone, e.g. events/appointments) flows
   * through to the Stripe billing details / customer profile.
   */
  private get effectivePhone(): string {
    return this.phoneEdited
      ? this.draft.phone
      : this.draft.phone || this.customer.phone || "";
  }

  /**
   * ST3: whether the required-phone rule (if on) is currently satisfied.
   *
   * "If on" now has two sources — the embed's `requirePhone` option and the
   * org's `requiredSignupFields.phoneNumber`. Only the second is enforced by
   * the API, so ORing them is what keeps the button state honest.
   */
  private get phoneRequired(): boolean {
    return isPhoneRequired(this.requirePhone, this.requiredSignupFields);
  }

  private get phoneSatisfied(): boolean {
    return this.phoneRequired
      ? checkoutMath.hasValidPhone(this.effectivePhone)
      : !checkoutMath.hasInvalidOptionalPhone(this.effectivePhone);
  }

  /**
   * Whether the org's required date-of-birth / emergency-contact fields are
   * filled in. Only gates the guest path: a signed-in member already has a
   * roster, so `create-guest` never runs and the policy does not apply to them.
   */
  private get requiredProfileSatisfied(): boolean {
    if (!this.needsDetails) return true;
    return isRequiredProfileComplete(
      this.requiredSignupFields,
      this.requiredProfile
    );
  }

  private get detailsValid(): boolean {
    if (!this.usingNewCard) return true; // saved card → member already known
    const c = this.effectiveCustomer;
    return (
      !!(c.firstName && c.lastName && c.email) &&
      this.phoneSatisfied &&
      this.requiredProfileSatisfied
    );
  }

  private get anyWallet(): boolean {
    return this.applePay || this.googlePay;
  }

  private get usingWallet(): boolean {
    return (
      this.usingNewCard &&
      (this.payType === "apple-pay" || this.payType === "google-pay")
    );
  }

  /**
   * ACH is a US bank debit, so — as in the dashboard's PaymentTypeSelector —
   * it is offered only when the org charges in USD.
   */
  private get achAvailable(): boolean {
    return (
      (this.currency || this.stripe?.currency || "USD").toUpperCase() === "USD"
    );
  }

  private get usingBankAccount(): boolean {
    return this.usingNewCard && this.payType === "ach" && this.achAvailable;
  }

  /**
   * Fee rail for both the quote and the charge. They must agree, or the buyer
   * is quoted the ACH fee and charged the card fee.
   */
  private get chargeRail(): "us_bank_account" | "card" {
    return this.usingBankAccount ? "us_bank_account" : "card";
  }

  /** The Stripe card field is on screen: a new card, not a wallet or bank. */
  private get usingCardField(): boolean {
    return this.usingNewCard && !this.usingWallet && !this.usingBankAccount;
  }

  /** Prefilled from the buyer's name until they edit it. */
  private get effectiveBankAccountName(): string {
    if (this.bankAccountNameEdited) return this.bankAccountName;
    const c = this.effectiveCustomer;
    return `${c.firstName} ${c.lastName}`.trim();
  }

  /**
   * The numbers to display and to authorize a wallet against.
   *
   * A quote from the server whenever we have one — it is the only source that
   * knows the Stripe Tax the invoice will assess and the platform fee an org
   * passes on (`passFeesToCustomer`). Until then, the cart's own arithmetic,
   * flagged `quoted: false` so nothing prints a tax figure it made up.
   */
  private get totals(): OrderTotals {
    return this.quote ?? estimatedTotals(this.subtotal, this.discount);
  }

  private get totalCents(): number {
    return this.totals.totalInCents;
  }

  /** Quoted total in dollars, for the copy and events that speak in dollars. */
  private get totalDue(): number {
    return this.totalCents / 100;
  }

  /**
   * The roster a quote is drafted against, matching exactly what the charge
   * will send — same beneficiary, same capability. Quoting against anything
   * else would print a number the charge then contradicts.
   *
   * Null when nothing has created a roster yet (a host that collects the
   * buyer's details inside this element rather than in a step of its own):
   * there is nothing to draft an invoice against, so the cart shows its
   * estimate until the charge itself resolves the rest.
   */
  private get quoteTarget(): QuoteTarget | null {
    if (this.memberContext) {
      return {
        purchasedForRosterId:
          this.purchasedForRosterId || this.memberContext.rosterId,
        stripeCustomerId: this.memberContext.stripeCustomerId,
      };
    }
    if (this.guestRoster) {
      return {
        purchasedForRosterId:
          this.purchasedForRosterId || this.guestRoster.rosterId,
        checkoutToken: this.guestRoster.checkoutToken,
        stripeCustomerId: this.guestRoster.stripeCustomerId,
      };
    }
    return null;
  }

  /**
   * Inputs the quote depends on. Re-quoting is driven from `updated()`, which
   * runs after every render — including the ones the quote itself causes — so
   * the fetch is keyed rather than fired on every pass.
   */
  private quoteKey = "";
  private quoteGen = 0;
  private quoteFormPreparation: Promise<void> | null = null;
  private isRequiredFormQuotePending = false;
  private quotedContext = "";
  private preparedBookingKey = "";
  private quotedChargeContext = "";

  private get isBookingPreparationRequired(): boolean {
    return this.bookingCheckout && !this.deferredItem;
  }

  private get hasCurrentBookingQuote(): boolean {
    return !this.isBookingPreparationRequired || !!(
      this.hasPreparedBookingPrerequisites &&
      this.quote?.quoted && this.quotedChargeContext === this.quoteContext()
    );
  }

  private get hasPreparedBookingPrerequisites(): boolean {
    return !!(this.bookingPrerequisites?.ready &&
      this.preparedBookingKey === this.bookingPrerequisites.key);
  }

  private assertCurrentBookingQuote(expectedContext = this.quoteContext()): void {
    if (!this.hasCurrentBookingQuote ||
      (this.isBookingPreparationRequired && expectedContext !== this.quoteContext()))
      throw new Error("Your booking details changed. Review the current total before paying.");
  }

  private async prepareBookingQuote(): Promise<void> {
    if (!this.isBookingPreparationRequired) return;
    const prerequisites = this.bookingPrerequisites;
    if (!prerequisites?.ready || this.hasPreparedBookingPrerequisites ||
      this.quoteFormPreparation || this.quoteError) return;
    this.quoteFormPreparation = Promise.resolve().then(() => this.beforeCharge?.())
      .then(() => {
        if (this.bookingPrerequisites?.ready && this.bookingPrerequisites.key === prerequisites.key)
          this.preparedBookingKey = prerequisites.key;
      }).catch((error: unknown) => {
        if (this.bookingPrerequisites?.key === prerequisites.key)
          this.quoteError = error instanceof Error ? error.message : "Could not save your booking details. Please try again.";
      }).finally(() => {
        this.quoteFormPreparation = null;
        this.requestUpdate();
      });
    this.requestUpdate();
    await this.quoteFormPreparation;
  }

  private quoteContext(): string {
    return JSON.stringify([this.orgId, this.quoteTarget, this.items,
      this.couponId, this.isBookingPreparationRequired ? this.bookingPrerequisites : null,
      ...(this.deferredItem ? [] : [this.selectedCardId, this.payType])]);
  }

  protected willUpdate(): void {
    const context = this.quoteContext();
    if (context === this.quotedContext) return;
    this.quotedContext = context;
    ++this.quoteGen;
    this.quoteKey = "";
    this.quotedChargeContext = "";
    const hadQuote = this.quote !== null;
    this.quote = null;
    if (!this.payable) this.isRequiredFormQuotePending = false;
    if (this.isBookingPreparationRequired || !this.isRequiredFormQuotePending) this.quoteError = null;
    if (this.isBookingPreparationRequired) {
      ++this.walletSetupGen;
      this.discardWalletButton();
    }
    if (hadQuote) this.emitQuote();
  }

  /**
   * Guests are quoted against a roster, and a guest only gets one when
   * `create-guest` runs — which used to happen at the moment of charge.
   *
   * So mint it as soon as the payment step opens instead. This is not "early"
   * in any meaningful sense: the host only mounts this element once the buyer
   * has completed and submitted its details step, which is the same explicit
   * action the customer app's public checkout creates its guest roster on.
   * What it buys is a roster to draft the quote against, so the buyer sees the
   * tax and fees BEFORE handing over a card — and so an Apple/Google Pay sheet
   * authorizes the real total rather than the pre-tax one.
   *
   * Quoting attempts only once per element. A host that must save an owned
   * form before charging may explicitly retry a transient failure; ordinary
   * re-renders must not create guest profiles repeatedly. A confirmed
   * account-exists response is terminal because create-guest is find-or-401.
   */
  private guestRosterAttempted = false;
  private guestRosterTerminalError: string | null = null;
  private guestRosterPromise: Promise<GuestRosterHandle | undefined> | null =
    null;

  /**
   * Resolve the guest identity used by both quote and charge. Hosts with a
   * pre-charge prerequisite may await this public seam so they never race the
   * quote's in-flight guest creation and accidentally create the same email
   * twice.
   */
  async ensureGuestRoster(options?: {
    retryFailed?: boolean;
  }): Promise<GuestRosterHandle | undefined> {
    if (this.memberContext) return undefined;
    if (this.guestRoster) return this.guestRoster;
    if (this.guestRosterPromise) {
      const pending = await this.guestRosterPromise;
      if (pending || !options?.retryFailed) return pending;
    }
    if (this.guestRosterTerminalError) {
      if (options?.retryFailed) {
        throw new Error(this.guestRosterTerminalError);
      }
      return undefined;
    }
    if (this.guestRosterAttempted && !options?.retryFailed) return undefined;
    // `needsDetails` means the buyer is still typing their name and email into
    // this element — there is nothing settled enough to create a profile from.
    if (!this.payable || this.needsDetails || !this.detailsValid) {
      return undefined;
    }
    this.guestRosterAttempted = true;
    const cust = this.effectiveCustomer;
    this.guestRosterPromise = createGuestRoster(
      this.orgId,
      {
        firstName: cust.firstName,
        lastName: cust.lastName,
        email: cust.email,
        ...(this.effectivePhone ? { phone: this.effectivePhone } : {}),
        ...requiredProfilePayload(
          mergeRequiredProfile(this.requiredProfile, cust)
        ),
      },
      ...runtimeApiArguments(this.runtime)
    )
      .then((guest) => {
        this.guestRoster = guest;
        return guest;
      })
      .catch((error: unknown) => {
        // The anonymous path is closed to this email whenever the charge would
        // run — say so now rather than after they have entered a card.
        if (isAccountExistsError(error)) {
          this.error = accountExistsMessage(cust.email);
          this.guestRosterTerminalError = this.error;
          if (options?.retryFailed) {
            throw new Error(this.guestRosterTerminalError);
          }
        }
        // Other quote failures remain non-fatal. The charge path, or a host
        // prerequisite, may still retry through its established flow.
        return undefined;
      })
      .finally(() => {
        this.guestRosterPromise = null;
      });
    return this.guestRosterPromise;
  }

  private async ensureQuoteRoster(): Promise<QuoteTarget | null> {
    if (!this.quoteTarget) await this.ensureGuestRoster();
    return this.quoteTarget;
  }

  /**
   * Ask the server what this cart actually costs.
   *
   * Transport failures retain the estimate. An explicit server refusal is
   * different: this current cart cannot be charged until a valid retry.
   */
  private async refreshQuote(canPrepareRequiredForms = true): Promise<void> {
    if (this.deferredItem) {
      await this.refreshDeferredQuote();
      return;
    }
    await this.prepareBookingQuote();
    if (this.isBookingPreparationRequired &&
      (!this.hasPreparedBookingPrerequisites || this.quoteFormPreparation || this.quoteError)) return;
    const context = this.quoteContext();
    const target = (await this.ensureQuoteRoster()) ?? this.quoteTarget;
    if (context !== this.quoteContext() || !this.payable) return;
    if (!target) {
      if (this.isBookingPreparationRequired)
        this.quoteError = this.error || "Could not confirm your customer details. Please try again.";
      return;
    }
    const items = checkoutMath.attachCouponToFirst(this.items, this.couponId);
    const key = JSON.stringify([
      target.purchasedForRosterId,
      target.checkoutToken ?? "",
      this.selectedCardId,
      this.usingBankAccount,
      items,
    ]);
    if (key === this.quoteKey) return;
    this.quoteKey = key;
    const gen = ++this.quoteGen;
    try {
      const lines = await previewCharge(
        {
          orgId: this.orgId,
          items,
          purchasedForRosterId: target.purchasedForRosterId,
          stripeCustomerId: target.stripeCustomerId,
          // The fee differs by rail, so say which one rather than letting the
          // server assume card.
          paymentMethodType: this.chargeRail,
          ...(target.checkoutToken === undefined
            ? {}
            : { checkoutToken: target.checkoutToken }),
          ...(this.selectedCardId
            ? { paymentMethodId: this.selectedCardId }
            : {}),
        },
        ...runtimeApiArguments(this.runtime)
      );
      if (gen !== this.quoteGen || context !== this.quoteContext()) return;
      // A refused line carries an error instead of a price. Summing those
      // would quote a total for a cart that cannot be charged.
      const rejection = previewRejection(lines);
      if (rejection) {
        this.quote = null;
        this.quoteError = rejection;
        this.emitQuote();
        this.reportReadyState();
        // The host saves completed forms in beforeCharge. A preview enforces
        // those same forms, so waiting for Pay would leave this cart blocked
        // before its form-saving prerequisite could ever run.
        if (
          canPrepareRequiredForms &&
          !this.isBookingPreparationRequired &&
          this.beforeCharge &&
          lines.filter((line) => !line.valid).every(
            (line) => line.errorType === "form-required",
          )
        ) {
          this.isRequiredFormQuotePending = true;
          try {
            this.quoteFormPreparation ??= this.beforeCharge().finally(() => {
              this.quoteFormPreparation = null;
            });
            await this.quoteFormPreparation;
            if (gen !== this.quoteGen || context !== this.quoteContext()) return;
            this.quoteKey = "";
            // One preparation attempt per quote; a persistent refusal remains
            // blocking instead of repeatedly submitting the customer's form.
            await this.refreshQuote(false);
          } catch (error) {
            if (gen !== this.quoteGen || context !== this.quoteContext()) return;
            this.quoteError = error instanceof Error ? error.message : rejection;
            this.reportReadyState();
          }
        }
        return;
      }
      const quoted = totalsFromPreview(lines);
      if (!quoted.quoted) {
        if (this.isBookingPreparationRequired)
          this.quoteError = "We couldn’t confirm your current price. Please try again.";
        return;
      }
      this.isRequiredFormQuotePending = false;
      this.quotedChargeContext = context;
      this.quote = quoted;
      this.quoteError = null;
      this.emitQuote();
      this.reportReadyState();
    } catch {
      if (gen !== this.quoteGen || context !== this.quoteContext()) return;
      // Allow a later change to retry rather than latching the failure.
      if (this.isBookingPreparationRequired) {
        this.quoteError = "We couldn’t confirm your current price. Please try again.";
        this.reportReadyState();
      } else this.quoteKey = "";
    }
  }

  private async refreshDeferredQuote(): Promise<void> {
    if (!this.validDeferredCart) {
      const message =
        "Reserve one deferred membership at a time. Discounts and other products cannot be combined with this purchase.";
      if (this.error !== message) this.error = message;
      return;
    }
    const target = await this.ensureQuoteRoster();
    if (!target) return;
    const request = this.deferredTarget();
    const key = JSON.stringify([
      request.orgId,
      request.rosterId,
      request.purchasedForRosterId,
      request.stripeProductId,
      request.settings,
      request.bookingCheckout,
    ]);
    if (key === this.quoteKey) return;
    this.quoteKey = key;
    const generation = ++this.quoteGen;
    this.deferredPreview = null;
    this.deferredConsentAccepted = false;
    this.deferredTermsChanged = false;
    this.confirmedDeferredSetup = undefined;
    this.deferredAttemptId = undefined;
    try {
      const preview = await previewDeferredCheckout(request);
      const totals = deferredQuoteTotals(
        preview,
        this.bookingCheckout &&
          request.settings.triggerType === "first_booking",
      );
      if (generation !== this.quoteGen) return;
      this.deferredPreview = preview;
      this.quote = totals;
      this.error = null;
      this.emitQuote();
    } catch (error) {
      if (generation !== this.quoteGen) return;
      this.error = deferredCheckoutError(error);
      this.deferredTermsChanged = deferredTermsNeedReview(error);
    }
  }

  /**
   * Hand the quote to the host so its order summary shows the same figures as
   * the payment step, without previewing the cart a second time.
   */
  private emitQuote(): void {
    this.dispatchEvent(
      new CustomEvent("zipper-pay-quote", {
        detail: { totals: this.quote, deferredPreview: this.deferredPreview },
        bubbles: true,
        composed: true,
      })
    );
  }

  async firstUpdated(): Promise<void> {
    // Default to the member's saved/default card when present (Phase 4).
    if (this.savedCards.length) {
      const def =
        this.savedCards.find((c) => c.isDefault) ?? this.savedCards[0];
      this.selectedCardId = def.paymentMethodId;
    }
    if (!this.payable) {
      this.status = "idle";
      this.walletProbeDone = true;
      return;
    }
    await this.bootstrapStripe();
  }

  /**
   * PLAT-5624: `firstUpdated` runs ONCE. It used to be the only place Stripe
   * was loaded, and it bails when the cart isn't purchasable — so an element
   * that mounted before its items were resolved (the appointment payment step
   * opens before the pay-options lookup lands) stayed permanently dead: the
   * host could hand over perfectly good items later, `payable` would flip, the
   * form would render its `#zpay-card` slot — and no Stripe Element would ever
   * be created for it. Bootstrapping is idempotent and re-driven from
   * `updated()`, so late-arriving items recover on their own.
   */
  private stripeBootstrapped = false;

  private async bootstrapStripe(): Promise<void> {
    if (this.stripeBootstrapped || !this.payable) return;
    this.stripeBootstrapped = true;
    try {
      this.stripeApi = await loadEmbedStripe(this.stripe!);
      if (!this.stripeApi) throw new Error("Stripe failed to load");
      this.elements = this.stripeApi.elements();
      await this.setupWallet();
      this.status = "idle";
      await this.updateComplete;
      this.mountCard();
      this.mountWalletButton();
    } catch (e) {
      // Let a later retry try again rather than latching the failure forever.
      this.stripeBootstrapped = false;
      this.error =
        e instanceof Error ? e.message : "Could not load payment form";
      this.status = "idle";
      this.walletProbeDone = true;
    }
  }

  protected updated(changed: Map<string, unknown>): void {
    // PLAT-5624: items can become purchasable after we mounted — see
    // bootstrapStripe.
    if (!this.stripeBootstrapped && this.payable) {
      void this.bootstrapStripe();
    }
    // Price the cart against the server (tax + pass-on fees). Keyed on the
    // cart, so re-renders caused by the quote itself don't re-fetch it.
    void this.refreshQuote();
    // Rebuild wallet when discount/items change so Apple/Google authorize the
    // post-coupon total (dashboard WalletPaymentButton parity — PLAT-4602).
    // PLAT-6792: the server quote too — it adds tax and pass-on fees, and the
    // sheet otherwise authorizes the pre-quote estimate. setupWallet skips the
    // rebuild when the total is unchanged.
    if (
      this.stripeApi &&
      (changed.has("items") ||
        changed.has("discount") ||
        changed.has("heading") ||
        changed.has("bookingPrerequisites") ||
        changed.has("memberContext") ||
        changed.has("guestRoster") ||
        changed.has("selectedCardId") ||
        changed.has("payType") ||
        changed.has("quote"))
    ) {
      void this.setupWallet().then(() => {
        this.mountWalletButton();
      });
    }
    if (this.usingCardField) {
      this.mountCard();
    }
    if (this.usingWallet) {
      this.mountWalletButton();
    }
    // B2: a re-render can replace a slot placeholder — re-seat its projection.
    this.syncAllPortals();
    // B3: let the caller's own submit button react to validity changes.
    this.reportReadyState();
    // PLAT-5624: …and to charge-in-flight changes, so its CTA can't be clicked
    // a second time while the first charge is still running.
    this.reportBusyState();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    // B5: the mount targets go away with us, so the Elements have to die too —
    // NOT just unmount. Leaving `cardEl` set with `cardMounted` still true is
    // how you get "We could not retrieve data from the specified Element":
    // a reconnect (a host re-rendering the step body that contains us, or
    // re-parenting us) finds `cardMounted === true`, skips `mountCard()`
    // entirely, and the next charge reads an Element whose iframe is gone.
    this.discardCard();
    this.discardWalletButton();
    this.teardownPortals();
  }

  connectedCallback(): void {
    super.connectedCallback();
    // B5: re-parented after a disconnect. `disconnectedCallback` discarded the
    // Elements, and nothing else re-runs `mountCard()` when no reactive
    // property changed — Lit does not re-render on reconnect — so rebuild here
    // or the payment step comes back with a dead card field.
    if (this.hasUpdated) {
      void this.updateComplete.then(() => {
        if (!this.isConnected) return;
        if (this.usingCardField) this.mountCard();
        if (this.usingWallet) this.mountWalletButton();
      });
    }
  }

  /**
   * B5: end the current card Element's life, together with its mount target.
   *
   * Every path that tears the portal down — disconnect, switching to a saved
   * card, switching to a wallet — leaves the Element pointing at a node that is
   * no longer in the document. Stripe still hands you the object; it's
   * `createPaymentMethod` that then fails with "We could not retrieve data from
   * the specified Element…". So the reference dies WITH its target and
   * `mountCard()` builds a fresh one, rather than resurrecting a reference
   * Stripe can no longer read.
   *
   * The cost is that a half-typed card isn't preserved across a wallet toggle.
   * That's the right trade: `destroy()` is unrecoverable by design, and a
   * silently unchargeable form is far worse than re-typing a card.
   */
  private discardCard(): void {
    for (const field of [this.cardEl, this.cardExpiryEl, this.cardCvcEl]) {
      try {
        field?.unmount();
      } catch {
        /* already unmounted */
      }
      try {
        field?.destroy();
      } catch {
        /* already destroyed */
      }
    }
    this.cardEl = null;
    this.cardExpiryEl = null;
    this.cardCvcEl = null;
    this.cardMounted = false;
    this.cardMountTarget = null;
    this.cardDetailMountTargets = [];
    for (const field of Object.values(this.cardValidity)) {
      field.ready = false;
      field.complete = false;
      field.error = "";
    }
    this.cardReady = false;
    this.cardComplete = false;
    this.cardFieldError = "";
    this.cardReportsChanges = false;
    this.destroyPortal("card");
    this.destroyPortal("cardExpiry");
    this.destroyPortal("cardCvc");
  }

  /** B5: same lifecycle discipline for the wallet button's Element. */
  private discardWalletButton(): void {
    try {
      this.prButtonEl?.unmount();
      this.prButtonEl?.destroy();
    } catch {
      /* already unmounted/destroyed */
    }
    this.prButtonEl = null;
    this.prButtonMounted = false;
    this.destroyPortal("prButton");
  }

  /**
   * Apple Pay / Google Pay via Stripe Payment Request. Mirrors dashboard
   * `useWalletAvailability` + `WalletPaymentButton`: probe `canMakePayment()`,
   * expose apple/google flags, and rebuild when the charged amount changes.
   */
  private async setupWallet(): Promise<void> {
    if (!this.stripeApi) return;
    const cents = this.totalCents;
    if (cents <= 0 || this.deferredItem) {
      this.teardownWallet();
      this.applePay = false;
      this.googlePay = false;
      this.walletProbeDone = true;
      if (this.usingWallet) this.payType = "card";
      return;
    }
    // Same amount already probed — keep existing PaymentRequest.
    const context = this.quoteContext();
    if (this.paymentRequest && this.walletAmountCents === cents &&
      (!this.isBookingPreparationRequired || this.walletSetupContext === context)) return;

    const gen = ++this.walletSetupGen;
    this.teardownWallet();
    this.walletProbeDone = false;

    try {
      // PLAT-6792: the connected account's own country and currency — NZ, AU,
      // CA and GB studios take payments, and a US/usd request misstates both.
      const pr = this.stripeApi.paymentRequest({
        // Stripe requires the connected account's country here, and a
        // currency matching the charge, or a GBP charge shows "$".
        country: (this.stripe?.country || "US").toUpperCase(),
        currency: (
          this.currency ||
          this.stripe?.currency ||
          "USD"
        ).toLowerCase(),
        total: {
          label: this.heading || "Total",
          amount: cents,
        },
        requestPayerName: this.needsDetails,
        requestPayerEmail: this.needsDetails,
        requestPayerPhone: this.phoneRequired,
      });
      const result = await pr.canMakePayment();
      if (gen !== this.walletSetupGen) return; // stale probe
      if (!result) {
        this.applePay = false;
        this.googlePay = false;
        this.walletProbeDone = true;
        if (this.usingWallet) this.payType = "card";
        return;
      }
      this.paymentRequest = pr;
      this.walletSetupContext = context;
      this.walletAmountCents = cents;
      this.applePay = !!result.applePay;
      this.googlePay = !!result.googlePay;
      this.walletProbeDone = true;
      // If selection pointed at an unavailable wallet, fall back to card.
      if (this.payType === "apple-pay" && !this.applePay) this.payType = "card";
      if (this.payType === "google-pay" && !this.googlePay)
        this.payType = "card";
      pr.on("paymentmethod", (ev) => this.handleWalletPayment(ev, gen));
    } catch (e) {
      if (gen !== this.walletSetupGen) return;
      // Hidden wallets are otherwise undiagnosable on a customer's site.
      console.warn("[zipper-embed] wallet availability check failed", e);
      this.applePay = false;
      this.googlePay = false;
      this.walletProbeDone = true;
    }
  }

  private teardownWallet(): void {
    // B5: the button Element belongs to the PaymentRequest we're dropping —
    // destroy it (and its portal) rather than leaving a detached reference the
    // next mount could pick up.
    this.discardWalletButton();
    this.paymentRequest = null;
    this.walletAmountCents = -1;
    this.walletSetupContext = "";
  }

  /**
   * B2: resolve the real Stripe mount node for `slot` (an in-template
   * placeholder). If `slot` is already document-rooted (e.g. the standalone
   * overlay presentation, or a test that appends `<zipper-payment>` straight
   * to `document.body`), mount directly in it — no portal needed. Otherwise
   * lazily create (or reuse) a document-rooted div projected into `slot`
   * through named slots, and return that.
   */
  private resolveMountTarget(
    slot: HTMLElement,
    which: PaymentPortalKey
  ): HTMLElement {
    return this.portalController.resolveMountTarget(slot, which);
  }

  private syncAllPortals(): void {
    this.portalController.sync();
  }

  private teardownPortals(): void {
    this.portalController.teardown();
  }

  private destroyPortal(which: PaymentPortalKey): void {
    this.portalController.destroy(which);
  }

  private mountWalletButton(): void {
    if (
      this.submission.paymentCommitted ||
      !this.hasCurrentBookingQuote ||
      !this.usingWallet ||
      this.prButtonMounted ||
      !this.elements ||
      !this.paymentRequest
    ) {
      return;
    }
    const node = this.querySelector<HTMLElement>("#zpay-prbutton");
    if (!node) return;
    this.prButtonEl = this.elements.create("paymentRequestButton", {
      paymentRequest: this.paymentRequest,
      style: {
        paymentRequestButton: {
          type: "buy",
          theme: "dark",
          height: PR_BUTTON_HEIGHT,
        },
      },
    });
    this.prButtonEl.mount(this.resolveMountTarget(node, "prButton"));
    this.prButtonMounted = true;
  }

  private selectPayType(type: PaymentType): void {
    if (type === "apple-pay" && !this.applePay) return;
    if (type === "google-pay" && !this.googlePay) return;
    if (type === "ach" && !this.achAvailable) return;
    this.payType = type;
    this.error = null;
    if (type === "card") {
      this.discardWalletButton();
    } else {
      // B5: the card portal is destroyed here, so the card Element dies with
      // it. `updated()` rebuilds a fresh one when the buyer comes back to card.
      this.discardCard();
      this.discardWalletButton();
      void this.updateComplete.then(() => this.mountWalletButton());
    }
  }

  /**
   * Charge from an Apple/Google Pay PaymentMethod. Reuses the guest checkout
   * path; the wallet also carries payer name/email/phone, so when the caller
   * didn't pre-fill details (e.g. shop) we take them from the wallet.
   */
  private async handleWalletPayment(
    ev: PaymentRequestPaymentMethodEvent,
    walletGeneration = this.walletSetupGen,
  ): Promise<void> {
    // PLAT-5624: the wallet sheet can fire twice (re-authorize / rebuilt
    // PaymentRequest) and shares the submission actor with the CTA paths, so a wallet
    // authorization cannot stack on top of an in-flight card charge.
    if (this.quoteError || !this.hasCurrentBookingQuote ||
      (this.isBookingPreparationRequired && (!this.usingWallet || walletGeneration !== this.walletSetupGen)) ||
      this.isTerminal || !this.submission.claim()) {
      ev.complete(this.submission.paymentCommitted ? "success" : "fail");
      return;
    }
    let charged = false;
    this.error = null;
    this.status = "paying";
    this.reportBusyState();
    try {
      let cust: GuestDetails & { phone?: string } = this.effectiveCustomer;
      if (this.needsDetails) {
        const parts = (ev.payerName || "").trim().split(/\s+/);
        cust = {
          firstName: parts.shift() || "",
          lastName: parts.join(" "),
          email: ev.payerEmail || "",
          // E4: the wallet sheet returns the payer's phone when we asked for it
          // (`requestPayerPhone`) — carry it through so it reaches the profile.
          phone: ev.payerPhone || undefined,
        };
      }
      const walletPhone = cust.phone || this.effectivePhone;
      if (
        this.phoneRequired
          ? !checkoutMath.hasValidPhone(walletPhone)
          : checkoutMath.hasInvalidOptionalPhone(walletPhone)
      ) {
        throw new Error(
          "Enter a valid phone number; include + and a country code if needed.",
        );
      }
      await this.beforeCharge?.();
      this.assertCurrentBookingQuote();
      if (this.isBookingPreparationRequired && walletGeneration !== this.walletSetupGen)
        throw new Error("Your payment selection changed. Review the current total before paying.");
      const invoiceIds = await this.completeNewCardCharge(
        ev.paymentMethod.id,
        cust
      );
      charged = true;
      ev.complete("success");
      await this.completeConfirmation(invoiceIds);
    } catch (e) {
      ev.complete("fail");
      this.error = e instanceof Error ? e.message : "Payment failed";
      this.status = "idle";
    } finally {
      this.submission.release(charged, this.error ?? "");
      this.reportBusyState();
    }
  }

  private mountCard(): void {
    if (this.submission.paymentCommitted || this.cardMounted || !this.elements)
      return;
    const node = this.querySelector<HTMLElement>("#zpay-card");
    const expiry = this.querySelector<HTMLElement>("#zpay-card-expiry");
    const cvc = this.querySelector<HTMLElement>("#zpay-card-cvc");
    if (!node || !expiry || !cvc) return;
    if (!this.cardEl) {
      const options = {
        style: {
          base: { fontSize: "16px", color: "#1f2a22" },
          invalid: { color: "#b4541f" },
        },
      };
      this.cardEl = this.elements.create("cardNumber", options);
      this.cardExpiryEl = this.elements.create("cardExpiry", options);
      this.cardCvcEl = this.elements.create("cardCvc", options);
      this.watchCardLifecycle(this.cardEl, "number", node);
      this.watchCardLifecycle(this.cardExpiryEl, "expiry", expiry);
      this.watchCardLifecycle(this.cardCvcEl, "cvc", cvc);
    }
    const target = this.resolveMountTarget(node, "card");
    const expiryTarget = this.resolveMountTarget(expiry, "cardExpiry");
    const cvcTarget = this.resolveMountTarget(cvc, "cardCvc");
    this.cardMountTarget = target;
    this.cardDetailMountTargets = [expiryTarget, cvcTarget];
    // Block the actual light-DOM mounts too: inert on a shadow wrapper alone
    // cannot prevent focus on Stripe's portaled iframes.
    this.syncCardInteractivity();
    this.cardEl.mount(target);
    this.cardExpiryEl!.mount(expiryTarget);
    this.cardCvcEl!.mount(cvcTarget);
    this.cardMounted = true;
    this.syncCardInteractivity();
    // PLAT-5624: `cardMounted` / `cardMountTarget` are not reactive, but
    // `canSubmit` reads them through `cardUsable` — and the standalone
    // overlay's own submit button renders `?disabled=${!this.canSubmit}`.
    // Without this the button would stay greyed out after a successful mount
    // until some unrelated state happened to trigger a render. Safe from
    // looping: `mountCard()` early-returns once `cardMounted` is true.
    this.requestUpdate();
  }

  /**
   * B4/B5: subscribe to the card Element's own lifecycle feed.
   *
   * Subscribed at creation time and never torn down or duplicated, because the
   * Element is created fresh by `mountCard()` and discarded wholesale by
   * `discardCard()` — the subscription's lifetime is exactly the Element's.
   *
   * - `ready` is Stripe telling us the iframe is live and readable. Charging
   *   before it produces "We could not retrieve data from the specified
   *   Element…", so it gates `cardUsable` (and therefore `canSubmit`).
   * - `change` carries `{ empty, complete, error }` — what makes `canSubmit`
   *   mean "this method can be charged" rather than "this method was clicked".
   *
   * Both call `reportReadyState()` inline: the Stripe iframe is outside Lit's
   * reactive graph, and the parent's CTA needs the flip on the keystroke that
   * completed the card, not on the next render.
   */
  private watchCardLifecycle(
    card: CardFieldLifecycle,
    key: keyof ZipperPayment["cardValidity"],
    slot: HTMLElement,
  ): void {
    if (typeof card.on !== "function") return;
    this.cardReportsChanges = true;
    card.on("ready", () => {
      this.cardValidity[key].ready = true;
      this.updateCardValidity();
    });
    card.on("change", (ev) => {
      this.cardValidity[key].complete = !!ev.complete;
      this.cardValidity[key].error = ev.error?.message ?? "";
      this.updateCardValidity();
    });
    card.on("focus", () => slot.parentElement?.classList.add("focused"));
    card.on("blur", () => slot.parentElement?.classList.remove("focused"));
  }

  private updateCardValidity(): void {
    const fields = Object.values(this.cardValidity);
    this.cardReady = fields.every((field) => field.ready);
    this.cardComplete = fields.every((field) => field.complete);
    this.cardFieldError = fields.find((field) => field.error)?.error ?? "";
    this.syncCardInteractivity();
    this.reportReadyState();
  }

  private get cardFieldsLoading(): boolean {
    return !this.cardMounted || (this.cardReportsChanges && !this.cardReady);
  }

  private syncCardInteractivity(): void {
    const loading = this.cardFieldsLoading;
    for (const target of [this.cardMountTarget, ...this.cardDetailMountTargets]) {
      if (!target) continue;
      target.inert = loading;
      // Preserve layout and the mounted iframe while it initializes.
      target.style.opacity = loading ? "0" : "";
    }
  }

  /** Switch between a saved card and the new-card form. */
  private selectCard(paymentMethodId: string): void {
    this.selectedCardId = paymentMethodId;
    this.error = null;
    if (paymentMethodId) {
      // B5: moving to a saved card tears both portals down, so the Elements go
      // with them — see discardCard(). Coming back to "use a new card" builds
      // a fresh Element rather than remounting a reference Stripe can't read.
      this.discardCard();
      this.discardWalletButton();
      this.payType = "card";
    } else {
      void this.updateComplete.then(() => {
        this.mountCard();
        if (this.usingWallet) this.mountWalletButton();
      });
    }
  }

  /**
   * Shared checkout for a freshly-created PaymentMethod (typed card OR
   * Apple/Google Pay wallet). Attaches the validated coupon to the first item so
   * the backend applies it invoice-wide, then runs the post-charge step.
   */
  private async completeNewCardCharge(
    paymentMethodId: string,
    cust: GuestDetails & { phone?: string }
  ): Promise<(string | undefined)[]> {
    const items = checkoutMath.attachCouponToFirst(this.items, this.couponId);
    let invoiceIds: (string | undefined)[];
    if (this.memberContext) {
      const result = await runMemberCheckout({
        orgId: this.orgId,
        rosterId: this.memberContext.rosterId,
        stripeCustomerId: this.memberContext.stripeCustomerId,
        paymentMethodId,
        // A connected bank's SetupIntent already attached it.
        attachPaymentMethod: !this.usingBankAccount,
        // Charge on the same rail the quote priced.
        paymentMethodType: this.chargeRail,
        items,
        chargeAttemptId: this.submission.attemptId,
        purchasedForRosterId: this.purchasedForRosterId || undefined,
        ...runtimeApiOption(this.runtime),
      });
      invoiceIds = result.invoiceIds;
    } else {
      // E4: the guest roster minted by this charge IS the customer profile the org
      // sees afterwards, so the phone has to ride along with the name/email —
      // otherwise the profile is created with "(no phone number)" even though the
      // buyer typed one (here, or up front in the caller's details step).
      // A corrected inline phone takes precedence over an invalid caller value.
      // The wallet sheet's payer phone still wins when it supplied the customer.
      const phone =
        this.usingWallet && this.needsDetails
          ? cust.phone || this.effectivePhone
          : this.phoneEdited
            ? this.draft.phone
            : this.effectivePhone || cust.phone;
      const result = await runGuestCheckout({
        orgId: this.orgId,
        guest: {
          firstName: cust.firstName,
          lastName: cust.lastName,
          email: cust.email,
          phone: phone || undefined,
          // PLAT-4456 org-required fields. Sent unconditionally when present
          // rather than only when the org demands them: the customer typed them,
          // and the roster this charge mints IS the profile the org sees.
          //
          // Merged with `cust`, because the buyer usually answered these on the
          // HOST's details step, not here — `needsDetails` is false once the host
          // pre-fills a customer, so this element never renders the fields and
          // its own draft stays empty. Reading only the draft is what made
          // `create-guest` 400 with "Date of birth is required for signup in this
          // organization." on a checkout that had just collected the date.
          ...requiredProfilePayload(
            mergeRequiredProfile(this.requiredProfile, cust),
          ),
        },
        paymentMethodId,
        items,
        // PLAT-5624: server-side idempotency. `charge-multiple-guest` only runs
        // ChargeAttemptService dedupe when a key is supplied, and the embeds
        // never supplied one — so a duplicate delivery of this charge was billed
        // twice however fast the client-side guard was.
        chargeAttemptId: this.submission.attemptId,
        purchasedForRosterId: this.purchasedForRosterId || undefined,
        guestRoster: this.guestRoster,
        paymentMethodAttached: this.usingBankAccount,
        paymentMethodType: this.chargeRail,
        ...runtimeApiOption(this.runtime),
      });
      invoiceIds = result.invoiceIds;
    }
    this.commitPayment(invoiceIds);
    return invoiceIds;
  }

  private commitPayment(invoiceIds: (string | undefined)[]): void {
    this.paidInvoiceIds = invoiceIds;
    this.submission.markPaymentCommitted();
    this.dispatchEvent(
      new CustomEvent("zipper-pay-committed", {
        detail: { invoiceIds },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private async completeConfirmation(
    invoiceIds: (string | undefined)[],
  ): Promise<void> {
    try {
      // Stripe hosts escape the shadow tree; removing their slots alone leaves
      // live floating controls over the paid recovery panel.
      this.discardCard();
      this.discardWalletButton();
      await this.afterCharge?.(invoiceIds);
      this.fireSuccess(invoiceIds);
    } catch (e) {
      // The card is already charged. Keep the server's reason — a fixed
      // rejection (e.g. "Missing required form") fails every retry, and a
      // generic message leaves the customer re-paying from scratch.
      const reason = confirmationErrorReason(e);
      console.error("[zipper-embed] post-charge confirmation failed", {
        orgId: this.orgId,
        invoiceIds,
        reason,
        error: e,
      });
      this.error = reason
        ? `Your payment went through, but the booking couldn't be confirmed: ${reason} Retry without paying again, or contact the studio.`
        : "Confirmation couldn't be completed. Retry without paying again.";
      this.status = "idle";
      this.submission.fail(this.error);
      this.dispatchEvent(
        new CustomEvent("zipper-pay-recovery", {
          detail: { invoiceIds },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  private async retryConfirmation(): Promise<void> {
    if (!this.submission.recoveryRequired || !this.paidInvoiceIds) return;
    this.submission.retryFulfillment();
    this.error = null;
    this.status = "paying";
    this.reportBusyState();
    await this.completeConfirmation(this.paidInvoiceIds);
    this.reportBusyState();
  }

  private fireSuccess(invoiceIds: (string | undefined)[]): void {
    this.submission.markPaymentCommitted();
    this.submission.succeed({ invoiceIds });
    this.status = "done";
    this.dispatchEvent(
      new CustomEvent("zipper-pay-success", {
        detail: {
          total: this.totalDue,
          invoiceIds,
          deferredPurchase: this.reservedDeferredPurchase,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Presentation mode. `false` (default) renders the legacy centred overlay
   * (`.zpay-overlay` > `.zpay-modal[role=dialog]`) with a Cancel link.
   *
   * `true` renders the SAME body straight into wherever the caller appended
   * this element — no overlay, no backdrop, no `role="dialog"`/`aria-modal`,
   * no focus trap, no Cancel link (there is nothing to dismiss), and no inner
   * heading (the host step already has one). Method selection and the Stripe
   * card field are simply part of the host's payment step, and the host's own
   * primary button — gated on `canSubmit` / `zipper-pay-ready` — is the single
   * submit action.
   *
   * Inline does NOT change how Stripe mounts: Elements cannot live in a shadow
   * root, so the card still mounts into a document-rooted div projected into
   * `#zpay-card` (see the class doc / `resolveMountTarget`). Inline just moves
   * the slot.
   */
  @property({ type: Boolean }) inline = false;

  /**
   * B3 public API (see class doc): when `inline`, the parent's own primary
   * submit button ("Book & pay …" / "Book class …" / "Buy … ") is the ONLY
   * submit action rendered on the page — this component renders no inner
   * "Pay $N" there. Callers invoke `submitPayment()` directly; completion
   * fires the existing `zipper-pay-success` / `zipper-pay-close` events, and
   * `canSubmit` (+ the `zipper-pay-ready` event below) tells the caller when
   * it's actually safe to call.
   *
   * PLAT-5624: the standalone overlay presentation has no parent CTA, so it
   * keeps its own "Pay $N" (see `renderOverlaySubmit`) — which calls straight
   * into this same method, and therefore the same actor gate.
   */
  submitPayment(): Promise<void> {
    return this.pay();
  }

  /**
   * PLAT-5624: true while a charge is in flight, or after one has succeeded.
   *
   * Callers render their own primary CTA, so they need this (and the
   * `zipper-pay-busy` event below) to disable it and show a pending state the
   * moment the charge starts. `canSubmit` cannot do that job — it is false for
   * many benign reasons (half-typed card, wallet selected) that must NOT read
   * as "payment in progress".
   */
  get busy(): boolean {
    return this.status === "paying" || this.status === "done";
  }

  /** Charge finished or in flight — no further submit may be accepted. */
  private get isTerminal(): boolean {
    return this.submission.paymentCommitted || this.status === "done";
  }

  /**
   * Public readiness check the caller's own button can gate on directly.
   *
   * B4: "ready" means every precondition of a successful charge is met —
   * the org can be charged (`payable`), the buyer's identity fields are
   * complete (`detailsValid`), a payment method that THIS button can charge is
   * both selected and valid (`methodReady`), and no charge is already running
   * or finished (`status`). `loading` counts as not-ready: Stripe.js hasn't
   * resolved yet, so there is no card Element to charge.
   */
  get canSubmit(): boolean {
    return (
      this.payable &&
      this.hasCurrentBookingQuote &&
      !this.quoteError &&
      this.detailsValid &&
      this.methodReady &&
      (!this.deferredItem ||
        (this.validDeferredCart &&
          !this.deferredTermsChanged &&
          !!this.deferredPreview &&
          this.deferredConsentAccepted)) &&
      this.status === "idle" &&
      !this.submission.paymentCommitted
    );
  }

  /**
   * B4: is there a payment method the PARENT's CTA can actually charge?
   *
   * - Saved card on file → yes; nothing is left to enter.
   * - Apple / Google Pay → NO. Stripe's own PaymentRequest button is the submit
   *   action for a wallet (the sheet has to open from a real user gesture on
   *   Stripe's button), so the parent's CTA stays inert while a wallet is the
   *   selected method. That also closes a real hole: `selectPayType()` unmounts
   *   the card Element when a wallet is picked, so a CTA-driven `pay()` would
   *   have run `createPaymentMethod` against a detached Element.
   * - Typed card → only once Stripe reports the field complete and error-free.
   *   Until then the method is *selected* but not *usable*, which is exactly
   *   the state the greyed-out CTA has to represent.
   */
  private get methodReady(): boolean {
    if (!this.usingNewCard) return true;
    if (this.usingWallet) return false;
    // ACH: Stripe's bank modal opens from the CTA itself, so only the name
    // and a loaded Stripe.js have to be in place beforehand.
    if (this.usingBankAccount) {
      return !!this.stripeApi && !!this.effectiveBankAccountName.trim();
    }
    return this.cardUsable;
  }

  /**
   * B5: can Stripe actually READ the card Element right now?
   *
   * This is the precondition `createPaymentMethod` enforces, restated so we can
   * check it before calling instead of catching "We could not retrieve data
   * from the specified Element. Please make sure the Element you are attempting
   * to use is mounted and the ready event has been emitted." after the fact:
   *
   * - we still hold an Element and believe it is mounted;
   * - the node it was mounted into is still in the document (a portal that was
   *   destroyed, or a slot removed with the host's step body, detaches it);
   * - Stripe has emitted `ready` for it, and reports it complete and error-free.
   *
   * The `cardReportsChanges` escape hatch keeps a `{ mount, unmount }` test
   * double usable: with no lifecycle feed there is nothing to wait for, so the
   * mounted-ness checks stand alone.
   */
  private get cardUsable(): boolean {
    if (!this.cardEl || !this.cardMounted) return false;
    if (this.cardMountTarget && !this.cardMountTarget.isConnected) return false;
    if (this.cardDetailMountTargets.some((target) => !target.isConnected))
      return false;
    if (!this.cardReportsChanges) return true;
    return this.cardReady && this.cardComplete && !this.cardFieldError;
  }

  private lastReadyState: boolean | null = null;

  /** Fire `zipper-pay-ready` only when `canSubmit` actually changes value. */
  private reportReadyState(): void {
    const ready = this.canSubmit;
    if (ready === this.lastReadyState) return;
    this.lastReadyState = ready;
    this.dispatchEvent(
      new CustomEvent("zipper-pay-ready", {
        detail: { canSubmit: ready },
        bubbles: true,
        composed: true,
      })
    );
  }

  private lastBusyState: boolean | null = null;

  /**
   * PLAT-5624: fire `zipper-pay-busy` on every `busy` transition so the
   * caller's CTA can disable + show "Processing…" even for charges it did not
   * start itself (the Apple/Google Pay sheet submits from Stripe's own button).
   */
  private reportBusyState(): void {
    const busy = this.busy;
    if (busy === this.lastBusyState) return;
    this.lastBusyState = busy;
    this.dispatchEvent(
      new CustomEvent("zipper-pay-busy", {
        detail: { busy },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Single guarded entry point for every CTA-driven charge.
   *
   * PLAT-5624: the slot is claimed synchronously, before any validation that
   * could await and before `status` flips — the second half of a double-click
   * lands long before Lit has re-rendered the caller's `?disabled` CTA, so a
   * reactive flag on its own would not have stopped it.
   */
  private async pay(): Promise<void> {
    if (this.quoteError || !this.hasCurrentBookingQuote || this.isTerminal || !this.submission.claim()) return;
    let charged = false;
    try {
      charged = this.deferredItem
        ? await this.payDeferred()
        : !this.usingNewCard
          ? await this.payWithSavedCard()
          : this.usingBankAccount
            ? await this.payWithBankAccount()
            : await this.payWithNewCard();
    } finally {
      this.submission.release(charged, this.error ?? "");
      this.reportBusyState();
    }
  }

  private async payDeferred(): Promise<boolean> {
    if (!this.canSubmit || !this.stripeApi || !this.deferredPreview)
      return false;
    this.status = "paying";
    this.error = null;
    this.deferredTermsChanged = false;
    this.reportBusyState();
    try {
      await this.beforeCharge?.();
      this.assertCurrentBookingQuote();
      const target = this.deferredTarget();
      const key = JSON.stringify([
        target.orgId,
        target.rosterId,
        target.purchasedForRosterId,
        target.stripeProductId,
        target.settings,
        target.bookingCheckout,
      ]);
      if (
        !this.validDeferredCart ||
        key !== this.quoteKey ||
        !this.deferredConsentAccepted
      )
        throw new Error(
          "Please review this membership's current payment terms again.",
        );
      if (this.reservedDeferredPurchase && this.reservedDeferredKey !== key)
        throw new Error(
          "This membership has already been reserved. Please resume its original booking.",
        );
      if (!this.reservedDeferredPurchase) {
        const attemptId = (this.deferredAttemptId ??= this.submission.attemptId);
        if (!this.confirmedDeferredSetup) {
          const setup = await createDeferredCheckoutSetup(
            target,
            this.deferredPreview,
            attemptId,
          );
          if (setup.status === "succeeded" && setup.paymentMethodId) {
            this.confirmedDeferredSetup = {
              setupIntentId: setup.setupIntentId,
              paymentMethodId: setup.paymentMethodId,
            };
          } else {
            const paymentMethod = this.selectedCardId || {
              card: this.cardEl!,
              billing_details: {
                name: `${this.effectiveCustomer.firstName} ${this.effectiveCustomer.lastName}`.trim(),
                email: this.effectiveCustomer.email || undefined,
                phone: this.effectivePhone || undefined,
              },
            };
            const result = await this.stripeApi.confirmCardSetup(
              setup.clientSecret,
              { payment_method: paymentMethod },
            );
            if (result.error || result.setupIntent?.status !== "succeeded")
              throw new Error(
                result.error?.message ||
                  "Your card could not be authorized. Please try again.",
              );
            const method = result.setupIntent.payment_method;
            const paymentMethodId =
              typeof method === "string" ? method : method?.id;
            if (!paymentMethodId)
              throw new Error(
                "Your card authorization could not be confirmed. Please try again.",
              );
            this.confirmedDeferredSetup = {
              setupIntentId: result.setupIntent.id,
              paymentMethodId,
            };
          }
        }
        this.reservedDeferredPurchase = await reserveDeferredCheckout(
          target,
          this.deferredPreview,
          this.confirmedDeferredSetup,
          attemptId,
        );
        this.reservedDeferredKey = key;
      }
      const purchase = this.reservedDeferredPurchase;
      if (!["pending_charge", "active"].includes(purchase.status))
        throw new Error(
          "Your membership reservation needs payment review. Please contact the studio before trying another payment.",
        );
      if (this.afterDeferredPurchase)
        await this.afterDeferredPurchase(purchase);
      else await this.afterCharge?.([]);
      this.fireSuccess([]);
      return true;
    } catch (error) {
      this.error = deferredCheckoutError(error);
      this.deferredTermsChanged = deferredTermsNeedReview(error);
      if (this.deferredTermsChanged) this.deferredConsentAccepted = false;
      this.status = "idle";
      return false;
    }
  }

  /** @returns true only when the card was actually charged. */
  private async payWithNewCard(): Promise<boolean> {
    if (!this.detailsValid) {
      // Defense in depth: the inner "Pay $N" button used to gate on this via
      // `?disabled`. Now that the parent's own button drives `submitPayment()`,
      // guard here too so an out-of-sync caller can't fire an incomplete charge.
      this.error = "Please complete your details before paying.";
      return false;
    }
    if (this.usingWallet) {
      // B4: a wallet is authorized through Stripe's own PaymentRequest button.
      // The card Element is unmounted in this state, so charging from here
      // would fail inside createPaymentMethod with a confusing Stripe error.
      this.error = `Use the ${
        this.payType === "apple-pay" ? "Apple Pay" : "Google Pay"
      } button above to complete payment.`;
      return false;
    }
    if (!this.cardUsable) {
      // B4/B5: same defense in depth as `detailsValid` above. An out-of-sync
      // caller must not be able to fire a charge on a half-typed card, and —
      // more importantly — must not hand Stripe an Element it can't read
      // (not yet `ready`, or detached by a portal teardown / re-parent). Both
      // are recoverable states, so say which one and let them retry.
      this.error =
        this.cardFieldError ||
        (this.cardEl && this.cardReportsChanges && !this.cardReady
          ? "The card form is still loading — please try again in a moment."
          : "Please enter your card details.");
      return false;
    }
    if (!this.stripeApi || !this.cardEl) return false;
    this.error = null;
    this.status = "paying";
    // PLAT-5624: announce the pending state now, not on the next Lit update —
    // "the first one didn't respond" is what made the buyer click again.
    this.reportBusyState();
    const cust = this.effectiveCustomer;
    const quotedContext = this.quoteContext();
    try {
      await this.beforeCharge?.();
      this.assertCurrentBookingQuote(quotedContext);
      const { error, paymentMethod } = await this.stripeApi.createPaymentMethod(
        {
          type: "card",
          card: this.cardEl,
          billing_details: {
            name: `${cust.firstName} ${cust.lastName}`.trim(),
            email: cust.email || undefined,
            phone: this.effectivePhone || undefined,
            ...(this.billingPostalCode.trim()
              ? { address: { postal_code: this.billingPostalCode.trim() } }
              : {}),
          },
        }
      );
      if (error) throw new Error(error.message || "Card was declined");
      this.assertCurrentBookingQuote(quotedContext);

      // Shared with the wallet path: attach the coupon to the first item (backend
      // applies it invoice-wide), charge, run the post-charge step, then report.
      const invoiceIds = await this.completeNewCardCharge(
        paymentMethod!.id,
        cust
      );
      await this.completeConfirmation(invoiceIds);
      return true;
    } catch (e) {
      // This email already belongs to a member here, so the anonymous path is
      // closed to them. Hand them straight to sign-in rather than a dead-end
      // error — once signed in, their saved cards load and the member path
      // takes over. The card they just entered is not charged.
      if (isAccountExistsError(e)) {
        this.offerSignIn(cust.email);
        return false;
      }
      this.error = e instanceof Error ? e.message : "Payment failed";
      this.status = "idle";
      return false;
    }
  }

  /**
   * This email already belongs to a member here, so the anonymous path is
   * closed to them. Hand them straight to sign-in rather than a dead-end
   * error — once signed in, the member path takes over. Nothing was charged.
   */
  private offerSignIn(email: string): void {
    this.status = "idle";
    this.error = accountExistsMessage(email);
    openSignIn(this.orgId, this.accent, (firstName) => {
      this.error = null;
      this.dispatchEvent(
        new CustomEvent("zipper-member-signed-in", {
          detail: { firstName },
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  /**
   * ACH via Stripe Financial Connections (dashboard `ACHPaymentForm` parity).
   *
   * Unlike a card, the bank account is verified against a SetupIntent on the
   * buyer's Stripe customer, so the customer has to exist before Stripe's bank
   * modal opens: a guest's roster is minted first. The verified account stays
   * saved on the customer, then pays through the same charge as a card.
   *
   * @returns true only when the order was actually charged.
   */
  private async payWithBankAccount(): Promise<boolean> {
    if (!this.detailsValid) {
      this.error = "Please complete your details before paying.";
      return false;
    }
    const name = this.effectiveBankAccountName.trim();
    if (!name) {
      this.error = "Account holder name is required.";
      return false;
    }
    if (!this.stripeApi) {
      this.error = "Payment system not ready. Please try again.";
      return false;
    }
    this.error = null;
    this.status = "paying";
    this.reportBusyState();
    const cust = this.effectiveCustomer;
    const quotedContext = this.quoteContext();
    try {
      await this.beforeCharge?.();
      this.assertCurrentBookingQuote(quotedContext);
      const paymentMethodId =
        this.bankPaymentMethodId ||
        (await this.connectBankAccount(this.stripeApi, name, cust));
      if (!paymentMethodId) {
        this.status = "idle";
        return false;
      }
      this.assertCurrentBookingQuote(quotedContext);
      this.bankPaymentMethodId = paymentMethodId;
      const invoiceIds = this.memberContext
        ? await this.completeMemberCharge(paymentMethodId)
        : await this.completeNewCardCharge(paymentMethodId, cust);
      await this.completeConfirmation(invoiceIds);
      return true;
    } catch (e) {
      if (isAccountExistsError(e)) {
        this.offerSignIn(cust.email);
        return false;
      }
      this.error = e instanceof Error ? e.message : "Payment failed";
      this.status = "idle";
      return false;
    }
  }

  /**
   * Open Stripe's bank modal and return the verified PaymentMethod id, or null
   * (with `error` set) when the buyer cancelled or the bank still needs
   * micro-deposit verification — nothing may be charged in either case.
   */
  private async connectBankAccount(
    stripe: Stripe,
    name: string,
    cust: GuestDetails & { phone?: string }
  ): Promise<string | null> {
    const owner = this.memberContext
      ? { kind: "member" as const, rosterId: this.memberContext.rosterId }
      : await this.guestBankAccountOwner(cust);
    const clientSecret = await createAchSetupIntent(
      this.orgId,
      owner,
      ...runtimeApiArguments(this.runtime)
    );
    const collected = await stripe.collectBankAccountForSetup({
      clientSecret,
      params: {
        payment_method_type: "us_bank_account",
        payment_method_data: {
          billing_details: {
            name,
            ...(cust.email ? { email: cust.email } : {}),
          },
        },
      },
    });
    if (collected.error) {
      throw new Error(
        collected.error.message || "Failed to connect bank account"
      );
    }
    let intent = collected.setupIntent;
    if (!intent || intent.status === "requires_payment_method") {
      this.error = "Bank account setup was cancelled.";
      return null;
    }
    if (intent.status === "requires_confirmation") {
      const confirmed = await stripe.confirmUsBankAccountSetup(clientSecret);
      if (confirmed.error) {
        throw new Error(
          confirmed.error.message || "Failed to confirm bank account"
        );
      }
      intent = confirmed.setupIntent;
    }
    if (intent.status === "requires_action") {
      this.error =
        "Your bank account requires additional verification. Please check " +
        "your email for micro-deposit verification instructions.";
      return null;
    }
    const method = intent.payment_method;
    if (intent.status !== "succeeded" || !method) {
      throw new Error("Bank account setup did not complete. Please try again.");
    }
    return typeof method === "string" ? method : method.id;
  }

  /**
   * The guest roster the bank account is saved to. A host that collects the
   * buyer's details inside this element has no roster yet (the card path
   * mints it at charge time), so mint it here and keep it for the charge.
   */
  private async guestBankAccountOwner(
    cust: GuestDetails & { phone?: string }
  ): Promise<{ kind: "guest"; rosterId: number; checkoutToken: string }> {
    let guest =
      this.guestRoster ?? (await this.ensureGuestRoster({ retryFailed: true }));
    if (!guest) {
      guest = await createGuestRoster(
        this.orgId,
        {
          firstName: cust.firstName,
          lastName: cust.lastName,
          email: cust.email,
          ...(this.effectivePhone ? { phone: this.effectivePhone } : {}),
          ...requiredProfilePayload(
            mergeRequiredProfile(this.requiredProfile, cust)
          ),
        },
        ...runtimeApiArguments(this.runtime)
      );
      this.guestRoster = guest;
    }
    return {
      kind: "guest",
      rosterId: guest.rosterId,
      checkoutToken: guest.checkoutToken,
    };
  }

  /**
   * Charge a signed-in member using a saved card (Phase 4). Separate from the
   * guest `pay()` path: no Stripe Elements / createPaymentMethod — the card is
   * already on file, so we charge it directly via the authenticated endpoint.
   */
  private async payWithSavedCard(): Promise<boolean> {
    if (!this.memberContext) {
      // Shouldn't happen (picker only shows with a context) — fall back safely.
      this.error = "Your saved cards aren’t available — please enter a card.";
      this.selectCard("");
      return false;
    }
    this.error = null;
    this.status = "paying";
    // PLAT-5624: see payWithNewCard — surface the pending state immediately.
    this.reportBusyState();
    try {
      await this.beforeCharge?.();
      this.assertCurrentBookingQuote();
      const invoiceIds = await this.completeMemberCharge(this.selectedCardId);
      await this.completeConfirmation(invoiceIds);
      return true;
    } catch (e) {
      this.error = e instanceof Error ? e.message : "Payment failed";
      this.status = "idle";
      return false;
    }
  }

  /** Signed-in charge for a payment method already on the member's customer. */
  private async completeMemberCharge(
    paymentMethodId: string
  ): Promise<(string | undefined)[]> {
    const memberContext = this.memberContext!;
    const items = checkoutMath.attachCouponToFirst(this.items, this.couponId);
    const { invoiceIds } = await runMemberCheckout({
      orgId: this.orgId,
      rosterId: memberContext.rosterId,
      stripeCustomerId: memberContext.stripeCustomerId,
      paymentMethodId,
      items,
      // PLAT-5624: card-on-file is the most replay-prone path — the payment
      // method id is stable across attempts, so the same key + the same cart
      // is an exact fingerprint match and the API returns the cached result
      // instead of charging again.
      chargeAttemptId: this.submission.attemptId,
      purchasedForRosterId: this.purchasedForRosterId || undefined,
      // Charge on the same rail the quote priced.
      paymentMethodType: this.chargeRail,
      ...runtimeApiOption(this.runtime),
    });
    this.commitPayment(invoiceIds);
    return invoiceIds;
  }

  private close(): void {
    this.dispatchEvent(
      new CustomEvent("zipper-pay-close", { bubbles: true, composed: true })
    );
  }

  render(): TemplateResult {
    return html`
      ${paymentStyles}
      ${this.inline
        ? html`<div class="zpay-inline">${this.renderModalBody()}</div>`
        : html`<div
            class="zpay-overlay"
            @click=${(e: Event) => {
              if (e.target === e.currentTarget) this.close();
            }}
          >
            <div class="zpay-modal" role="dialog" aria-modal="true">
              ${this.renderModalBody()}
            </div>
          </div>`}
    `;
  }

  /**
   * Shared body for both presentations. The only structural difference is the
   * heading: inline, the host already labels the section it embedded us into
   * ("2 · Payment"), so repeating "Register · <event>" inside it is a duplicate
   * heading in the a11y tree as well as on screen.
   */
  private renderModalBody(): TemplateResult {
    return html`
      ${this.inline ? nothing : html`<h3>${this.heading}</h3>`}
      ${this.submission.paymentCommitted && !this.submission.succeeded
        ? html`<div class="zpay-recovery">
            <div role="status">
              <strong>Payment received</strong>
              <p>${this.error || "Completing your confirmation…"}</p>
            </div>
            <button
              class="zpay-btn"
              style=${`background:${this.accent}`}
              ?disabled=${!this.submission.recoveryRequired}
              aria-busy=${this.status === "paying" ? "true" : "false"}
              @click=${() => void this.retryConfirmation()}
            >
              ${this.status === "paying" ? "Confirming…" : "Retry confirmation"}
            </button>
          </div>`
        : this.payable
          ? this.renderForm()
          : this.renderUnavailable()}
    `;
  }

  /**
   * The money the buyer is agreeing to.
   *
   * Once quoted, this is the server's own breakdown — the same fields POS and
   * the customer app render (`CartSummary` / `InvoicePreview`). Before that it
   * says tax and fees are still to be calculated, because they are: printing
   * `Tax $0.00` next to a card that is about to be charged tax is the bug this
   * replaces.
   */
  private renderTotals(): TemplateResult {
    if (this.deferredItem) {
      const preview = this.deferredPreview;
      return html`<div class="zpay-total">
          <span>Due today</span
          ><span
            >${preview ? formatCents(this.totals.totalInCents, this.currency) : "Confirming terms…"}</span
          >
        </div>
        ${
          preview
            ? html`<p>
                  ${this.bookingCheckout && this.deferredItem.deferredChargeSettings?.triggerType === "first_booking" ? "Confirming this booking triggers your first membership charge immediately." : `First charge: ${preview.expectedChargeLabel}`}
                </p>
                <p>
                  First payment:
                  ${formatCents(preview.futureChargeAmountInCents, this.currency)}${preview.startupFeeAmountInCents ? ` (includes ${formatCents(preview.startupFeeAmountInCents, this.currency)} startup fee)` : ""}
                </p>
                ${preview.estimatedFeeAmountInCents ? html`<p>Fees: ${formatCents(preview.estimatedFeeAmountInCents, this.currency)}</p>` : nothing}
                ${preview.estimatedTaxAmountInCents ? html`<p>Estimated tax: ${formatCents(preview.estimatedTaxAmountInCents, this.currency)}</p>` : nothing}
                <label
                  ><input
                    type="checkbox"
                    .checked=${this.deferredConsentAccepted}
                    @change=${(event: Event) => {
                      this.deferredConsentAccepted = (
                        event.target as HTMLInputElement
                      ).checked;
                    }}
                  />
                  ${preview.consentText}</label
                >
                ${
                  this.error && this.deferredTermsChanged && !this.reservedDeferredPurchase
                    ? html`<button
                        type="button"
                        class="zpay-link"
                        @click=${() => {
                          this.quoteKey = "";
                          void this.refreshQuote();
                        }}
                      >
                        Review updated payment terms
                      </button>`
                    : nothing
                }`
            : html`<button
                type="button"
                class="zpay-link"
                @click=${() => {
                  this.quoteKey = "";
                  void this.refreshQuote();
                }}
              >
                Retry payment terms
              </button>`
        }`;
    }
    const t = this.totals;
    const line = (label: string, amount: string, muted = false) => html`
      <div
        class="zpay-total"
        style="font-weight:400${muted ? ";color:#516056" : ""}"
      >
        <span>${label}</span><span>${amount}</span>
      </div>
    `;
    return html`
      ${t.discountInCents
        ? line(
            "Discount",
            `−${formatCents(t.discountInCents, this.currency)}`,
            true
          )
        : nothing}
      ${t.quoted && t.platformFeeInCents
        ? line("Processing fee", formatCents(t.platformFeeInCents, this.currency))
        : nothing}
      ${t.quoted && t.taxInCents
        ? line("Tax", formatCents(t.taxInCents, this.currency))
        : nothing}
      <div class="zpay-total">
        <span>Total</span
        ><span>${formatCents(t.totalInCents, this.currency)}</span>
      </div>
      ${t.quoted
        ? nothing
        : html`<p class="zpay-total-note">
            Taxes and fees calculated at checkout
          </p>`}
    `;
  }

  private renderForm(): TemplateResult {
    // PLAT-6792: with saved cards the picker lists the wallets itself — a
    // member's default card is pre-selected, so a selector shown only for
    // "Use a new card" hid Apple/Google Pay from every returning member.
    // ACH is a method of its own, so it shows the selector without a wallet.
    const showMethods =
      !this.deferredItem &&
      !this.savedCards.length &&
      this.usingNewCard &&
      (this.anyWallet || this.achAvailable || !this.walletProbeDone);
    return html`
      ${this.renderTotals()}
      ${this.error ? html`<div class="zpay-err">${this.error}</div>` : nothing}
      ${this.quoteError ? html`<div class="zpay-err" role="alert">
        ${this.quoteError}
        <button class="zpay-link" @click=${() => void this.retryQuote()}>Try again</button>
      </div>` : nothing}
      ${this.isBookingPreparationRequired && !this.hasCurrentBookingQuote && !this.quoteError
        ? html`<p role="status">${!this.bookingPrerequisites?.ready
          ? "Complete your required booking details and cancellation acknowledgement before paying."
          : this.quoteFormPreparation ? "Saving your booking details…" : "Checking your current price…"}</p>`
        : nothing}
      ${this.savedCards.length ? this.renderCardPicker() : nothing}
      ${showMethods ? this.renderPayTypeSelector() : nothing}
      ${this.usingWallet && this.hasCurrentBookingQuote
        ? html`
            <p class="zpay-wallet-hint">
              Tap the button below to authorize payment with
              ${this.payType === "apple-pay" ? "Apple Pay" : "Google Pay"}.
            </p>
            <div id="zpay-prbutton"></div>
          `
        : nothing}
      ${this.needsDetails ? this.renderDetails() : nothing}
      ${this.usingNewCard &&
      !this.usingWallet &&
      !this.needsDetails &&
      (this.phoneRequired
        ? !checkoutMath.hasValidPhone(this.customer.phone)
        : checkoutMath.hasInvalidOptionalPhone(this.customer.phone))
        ? this.renderPhoneField()
        : nothing}
      ${this.usingBankAccount ? this.renderBankAccount() : nothing}
      ${this.usingCardField
        ? html`<div class="zpay-card-fields" ?data-loading=${this.cardFieldsLoading}>
              <div>
                <span class="zpay-field-label">Card number</span>
                <div class="zpay-card-input">
                  <div id="zpay-card" ?inert=${this.cardFieldsLoading} aria-busy=${String(this.cardFieldsLoading)}></div>
                  ${this.cardFieldsLoading ? html`<span class="zpay-card-loading" role="status">Loading payment fields…</span>` : nothing}
                </div>
              </div>
              <div class="zpay-card-details">
                <div>
                  <span class="zpay-field-label">Expiry date</span>
                  <div class="zpay-card-input">
                    <div id="zpay-card-expiry" ?inert=${this.cardFieldsLoading} aria-busy=${String(this.cardFieldsLoading)}></div>
                  </div>
                </div>
                <div>
                  <span class="zpay-field-label">Security code</span>
                  <div class="zpay-card-input">
                    <div id="zpay-card-cvc" ?inert=${this.cardFieldsLoading} aria-busy=${String(this.cardFieldsLoading)}></div>
                  </div>
                </div>
              </div>
              <label>
                <span class="zpay-field-label">Billing postal code</span>
                <input
                  class="zpay-input"
                  autocomplete="billing postal-code"
                  ?disabled=${this.cardFieldsLoading}
                  .value=${this.billingPostalCode}
                  @input=${(event: Event) => {
                    this.billingPostalCode = (
                      event.target as HTMLInputElement
                    ).value;
                  }}
                />
              </label>
            </div>
            ${
              // B4: the parent's CTA is greyed out while the card is
              // incomplete — say WHY, or the buyer is left staring at a dead
              // button. Rendered below the slot so it never displaces the
              // (possibly portaled) Stripe iframe's box.
              this.cardFieldError
                ? html`<div class="zpay-err" role="alert">
                    ${this.cardFieldError}
                  </div>`
                : nothing
            }`
        : nothing}
      ${
        // B3: INLINE has a parent-owned CTA. That button already mirrors the
        // child's busy event as "Processing…", so rendering the same status
        // here produces two competing progress labels in the checkout.
        this.inline
          ? nothing
          : // PLAT-5624: the STANDALONE overlay presentation has no parent CTA
            // by definition — it is a self-contained `role="dialog"` with a
            // backdrop and a Cancel link, and whatever appended it renders no
            // chrome around it. Removing the inner button for the inline hosts
            // therefore left this mode with a live Stripe card field and NO way
            // to submit it: a guaranteed dead end for anyone who mounts it.
            // (Every shipping caller now passes `inline`, so this is a
            // correctness backstop for the mode rather than a live bug — but a
            // documented presentation that cannot take a payment is a trap.)
            // Gated on the same `canSubmit` the inline hosts gate their CTA on,
            // and routed through `submitPayment()` → `pay()` so it shares the
            // one submission actor (synchronous claim + `chargeAttemptId`).
            this.renderOverlaySubmit()
      }
      ${this.inline
        ? nothing
        : html`<button class="zpay-link" @click=${() => this.close()}>
            Cancel
          </button>`}
    `;
  }

  /**
   * PLAT-5624: primary submit for the standalone overlay presentation.
   *
   * `?disabled` is presentation only — `pay()` claims the actor synchronously,
   * which is what actually stops the second half of a double-click (Lit
   * re-renders `?disabled` a microtask too late to help).
   */
  private renderOverlaySubmit(): TemplateResult {
    const busy = this.status === "paying";
    return renderPaymentSubmit({
      label: this.deferredItem
        ? this.bookingCheckout
          ? "Authorize card and confirm booking"
          : "Save card and reserve membership"
        : undefined,
      busy,
      disabled: busy || !this.canSubmit,
      accent: this.accent,
      total: this.totalDue,
      currency: this.currency,
      onSubmit: () => void this.submitPayment(),
    });
  }

  private renderPayTypeSelector(): TemplateResult {
    return renderPaymentTypeSelector({
      selected: this.payType,
      walletProbeDone: this.walletProbeDone,
      googlePay: this.googlePay,
      applePay: this.applePay,
      ach: this.achAvailable,
      onSelect: (type) => this.selectPayType(type),
    });
  }

  private renderBankAccount(): TemplateResult {
    return renderBankAccountForm({
      accountHolderName: this.effectiveBankAccountName,
      onNameChange: (value) => {
        this.bankAccountNameEdited = true;
        this.bankAccountName = value;
      },
    });
  }

  private renderCardPicker(): TemplateResult {
    return renderSavedCardPicker({
      cards: this.savedCards,
      selectedCardId: this.selectedCardId,
      selectedWallet:
        this.usingWallet || this.usingBankAccount ? this.payType : null,
      // A deferred reservation saves a card; wallets and ACH can't back it.
      applePay: !this.deferredItem && this.applePay,
      googlePay: !this.deferredItem && this.googlePay,
      ach: !this.deferredItem && this.achAvailable,
      onSelect: (paymentMethodId) => this.selectCard(paymentMethodId),
      onSelectWallet: (type) => {
        this.selectCard("");
        this.selectPayType(type);
      },
    });
  }

  private renderDetails(): TemplateResult {
    return renderPaymentCustomerDetails({
      draft: this.draft,
      requirePhone: this.phoneRequired,
      showPhone:
        this.phoneRequired ||
        this.phoneEdited ||
        checkoutMath.hasInvalidOptionalPhone(this.customer.phone),
      invalidPhone: checkoutMath.hasInvalidOptionalPhone(this.effectivePhone),
      effectivePhone: this.effectivePhone,
      onChange: (field, value) => {
        if (field === "phone") this.phoneEdited = true;
        this.draft = { ...this.draft, [field]: value };
      },
      requiredSignupFields: this.requiredSignupFields,
      requiredProfile: this.requiredProfile,
      onRequiredProfileChange: (field, value) => {
        this.requiredProfile = { ...this.requiredProfile, [field]: value };
      },
    });
  }

  private renderPhoneField(): TemplateResult {
    return html`${renderPaymentPhoneField({
      required: this.phoneRequired,
      value: this.effectivePhone,
      onChange: (value) => {
        this.phoneEdited = true;
        this.draft = { ...this.draft, phone: value };
      },
    })}${checkoutMath.hasInvalidOptionalPhone(this.effectivePhone)
      ? html`<p role="alert" class="zpay-err">
          Enter a valid phone number; include + and a country code if needed.
        </p>`
      : nothing}`;
  }

  private renderUnavailable(): TemplateResult {
    return renderPaymentUnavailable({
      stripeConfigured: !!this.stripe,
      inline: this.inline,
      accent: this.accent,
      onRetry: () => void this.retryUnavailable(),
      onClose: () => this.close(),
    });
  }

  /**
   * PLAT-5624: re-check purchasability and, when it now passes, bring the real
   * form up without forcing the visitor to restart checkout.
   *
   * `payable` is derived from `items`, which the host owns — so ask for a fresh
   * set (`zipper-pay-retry`) and re-run the Stripe/wallet bootstrap that
   * `firstUpdated()` skipped while we were unpayable.
   */
  private async retryUnavailable(): Promise<void> {
    this.error = null;
    this.dispatchEvent(
      new CustomEvent("zipper-pay-retry", { bubbles: true, composed: true })
    );
    // Give a synchronous host handler's new `items` a chance to land before
    // deciding whether we are purchasable now.
    await this.updateComplete;
    await this.bootstrapStripe();
  }

  /**
   * The "Try again" link, and the host's seam for a prerequisite that is not a
   * cart input — booking terms accepted after a required-form quote was
   * refused change nothing `updated()` re-quotes on. `requiredFormOnly` leaves
   * every other quote alone.
   */
  async retryQuote(options?: { requiredFormOnly?: boolean }): Promise<void> {
    if (
      options?.requiredFormOnly &&
      !(this.isRequiredFormQuotePending && this.quoteError)
    ) {
      return;
    }
    if (this.isBookingPreparationRequired) {
      this.quoteError = null;
      if (!this.quoteTarget) {
        try { await this.ensureGuestRoster({ retryFailed: true }); }
        catch (error: unknown) {
          this.quoteError = error instanceof Error ? error.message : "Could not confirm your customer details. Please try again.";
          return;
        }
      }
    }
    this.quoteKey = "";
    await this.refreshQuote();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "zipper-payment": ZipperPayment;
  }
}
